import { waitFor } from "xstate";

import { useLoggingStore, useMessageStore } from "@/stores";
import { PollingAbortController } from "@/stores/plugins/polling-plugin";

import { sleep } from "@/services/UtilitiesService";
import { actionAxiosRequest } from "@/services/AxiosWorker";

import { authMachine } from "@/machines/auth-machine";

import type { AxiosResponse } from "axios";

export interface ApiError<S extends number = number, D = any> {
  status: S;
  statusText: string;
  data?: D;
}

const NETWORK_TIMEOUT_MS = 30e3;

// Not sure how this is the condition when the above interface doesn't have the code property
export const isApiError = <T>(x: unknown): x is ApiError<number, T> => {
  return typeof (x as ApiError).status === "number";
};

/**
 * Classifies LLM API errors
 * - 207 - displays first advisory message; type=RecoverableError is treated as recovered
 * - 400 - recoverableError (warning) or contentPolicyViolationError (errored)
 * - Unknown errors: returns the original error
 */
export function classifyApiError(
  title: string,
  error: unknown,
):
  | { type: "recovered" }
  | { type: "error"; severity: "error" | "warn"; message: string }
  | { type: "error"; severity: "unknown"; message: unknown } {
  if (isApiError<Record<"advisories", string[]>>(error) && error.status === 207) {
    const [advisory] = error.data?.advisories ?? [];

    if (advisory != null) {
      const [type, message] = advisory.split(":");

      useMessageStore().updateMessage({
        type: "warning",
        autoHide: false,
        title,
        message,
        showReload: false,
      });

      if (type === "RecoverableError") return { type: "recovered" };
    }
  } else if (
    isApiError<
      | { hasError: true; recoverableError: string; contentPolicyViolationError: null }
      | { hasError: true; recoverableError: null; contentPolicyViolationError: string }
    >(error) &&
    error.status === 400
  ) {
    if (error.data?.recoverableError != null) {
      return { type: "error", severity: "warn", message: error.data.recoverableError };
    } else if (error.data?.contentPolicyViolationError != null) {
      return { type: "error", severity: "error", message: error.data.contentPolicyViolationError };
    }
  }

  return { type: "error", severity: "unknown", message: error };
}

export class Api {
  public auth = false;
  private baseURL: string;
  private feature: boolean;

  constructor(options: { apiUrl: string; feature: boolean }, auth: boolean) {
    this.auth = auth;

    this.baseURL = options.apiUrl;
    this.feature = options.feature;
  }

  public get token(): string {
    return authMachine.getSnapshot().context.token?.access_token ?? "";
  }

  public get<T>(url: string | string[], headers?: object, responseType?: string): Promise<T> {
    return this.executeRequest({
      verb: "get",
      url,
      config: { headers: this.getHeaders(headers), responseType },
      feature: this.feature,
    });
  }

  public getWithPollingSignal<T>(url: string | string[], headers?: object, responseType?: string): Promise<T> {
    return this.executeRequest({
      verb: "get",
      url,
      config: {
        headers: this.getHeaders(headers),
        responseType,
        signal: PollingAbortController?.signal,
      },
      feature: this.feature,
    });
  }

  public getWithObject<TParam extends Record<string, string | undefined>, T>(
    url: string | string[],
    payload: TParam,
    headers?: object,
  ): Promise<T> {
    return this.executeRequest({
      verb: "get",
      url,
      config: { headers: this.getHeaders(headers), params: payload },
      feature: this.feature,
    });
  }

  public post<TParam, T>(url: string | string[], payload?: TParam, headers?: object, multipart?: boolean): Promise<T> {
    return this.executeRequest({
      verb: "post",
      payload,
      url,
      config: { headers: this.getHeaders(headers) },
      multipart,
      feature: this.feature,
    });
  }
  public put<TParam, T>(url: string | string[], payload?: TParam, headers?: object): Promise<T> {
    return this.executeRequest({
      verb: "put",
      url,
      payload,
      config: { headers: this.getHeaders(headers) },
      feature: this.feature,
    });
  }
  public patch<TParam, T>(url: string | string[], payload?: TParam, headers?: object): Promise<T> {
    return this.executeRequest({
      verb: "patch",
      url,
      payload,
      config: { headers: this.getHeaders(headers) },
      feature: this.feature,
    });
  }
  public delete<TParam, T>(url: string | string[], payload?: TParam, headers?: object): Promise<T> {
    return this.executeRequest({
      verb: "delete",
      url,
      config: { headers: this.getHeaders(headers), data: payload },
      feature: this.feature,
    });
  }

  private getHeaders(headers?: object): object {
    headers = Object.assign(
      {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      headers,
    );

    return this.auth ? Object.assign({}, { Authorization: `bearer ${this.token}` }, headers) : headers;
  }

  private async executeRequest(parameters: {
    verb: string;
    url: string | string[];
    config?: object;
    payload?: unknown;
    multipart?: boolean;
    feature: boolean;
  }) {
    let retries = 0;

    await waitFor(authMachine, (snapshot) => snapshot.hasTag("Settled"));

    const snapshot = authMachine.getSnapshot();
    if (!snapshot.hasTag("AuthComplete")) return null as never;

    while (true) {
      try {
        if (!navigator.onLine) {
          await new Promise<void>((res) => window.addEventListener("online", () => res(), { once: true }));
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return await actionAxiosRequest(this.baseURL, parameters);
      } catch (_error) {
        const error = _error as AxiosResponse<{ retryAfterMs?: number } | undefined>;

        if (error.status === 503 && error.data?.retryAfterMs && ++retries < 10) {
          await sleep(error.data.retryAfterMs);
          continue;
        }

        if (error.status === 401 && ++retries <= 1) {
          authMachine.send({ type: "REFRESH_TOKEN" });
          await waitFor(authMachine, (state) => state.hasTag("AuthComplete"));

          parameters.config = {
            ...parameters.config,
            headers: this.getHeaders(),
          };

          continue;
        }

        await useLoggingStore().Error({
          message: error.statusText,
          data: error,
        });

        throw error;
      }
    }
  }
}
